
import React from 'react';
import { Link } from 'react-router-dom';
import { Package, Building, FileText } from 'lucide-react';
import { useLanguage } from '@/components/i18n/language-provider';
import { Separator } from '@/components/ui/separator';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const footerLinks = [
    {
      title: t('footer.product'),
      icon: <Package className="h-5 w-5 mb-2 text-primary" />,
      links: [
        { name: t('products.workforce').replace('Keystle', ''), href: "/products/workforce" },
        { name: t('products.b2b').replace('Keystle', '').replace('B2B', 'Business'), href: "/products/business" },
        { name: t('products.ciam').replace('Keystle', ''), href: "/products/ciam" },
      ]
    },
    {
      title: t('footer.company'),
      icon: <Building className="h-5 w-5 mb-2 text-primary" />,
      links: [
        { name: t('footer.about'), href: "/about" },
        { name: t('footer.contact'), href: "/contact" },
      ]
    },
    {
      title: t('footer.legal'),
      icon: <FileText className="h-5 w-5 mb-2 text-primary" />,
      links: [
        { name: "Privacy and Security", href: "/privacy-security" },
      ]
    }
  ];

  return (
    <footer className="border-t border-border/40 mt-24 pt-12 pb-8 bg-secondary/30">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-8">
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center mb-4">
              <img 
                src="/lovable-uploads/e0fc1014-cf06-43e7-8c6a-642a4174d074.png" 
                alt="Keystle Logo" 
                className="h-8 w-auto"
              />
              <span className="ml-2 text-xl font-display font-semibold">keystle</span>
            </Link>
            <p className="text-sm text-muted-foreground mb-6 max-w-xs">
              {t('footer.description')}
            </p>
            <div className="flex space-x-5 mb-8 md:mb-0">
              <a 
                href="https://www.linkedin.com/company/keystleidentity/" 
                className="text-muted-foreground hover:text-primary transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <img 
                  src="/lovable-uploads/348bffdf-1441-44fb-a503-dffadd25e368.png" 
                  alt="LinkedIn"
                  className="h-8 w-8 bg-transparent p-1 rounded-sm hover:bg-secondary/40 transition-colors"
                />
              </a>
            </div>
          </div>

          <div className="col-span-1 md:col-span-2 lg:col-span-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {footerLinks.map((section) => (
                <div key={section.title} className="group">
                  <div className="flex flex-col">
                    {section.icon}
                    <h3 className="font-medium text-base mb-4 group-hover:text-primary transition-colors duration-200">
                      {section.title}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {section.links.map((link) => (
                      <li key={link.name}>
                        <Link 
                          to={link.href} 
                          className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center"
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-border/60" />

        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-muted-foreground">
            © {currentYear} keystle. {t('footer.copyright')}
          </p>
          <p className="text-xs text-muted-foreground mt-4 md:mt-0">
            Made with ❤️ by the Keystle team
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
